import { useMemo } from 'react';
import { useQuery } from '@apollo/client';

import {
  LIVE_SESSIONS_QUERY,
  GET_LIVE_SESSION_BY_ID,
  GET_LIVE_VIDEO_RECORDING,
} from '../queries/liveSessions';
import { useOrgId } from './useOrgId';

const defaultOptions = {
  fetchPolicy: 'network-only',
};

const buildWhere = (organisationId, statuses) => {
  if (!organisationId) return null;
  return {
    _and: [
      { organisationId: { _eq: organisationId } },
      { status: { _in: statuses } },
    ],
  };
};

export const useLiveSessions = (limit = 100, statuses = ['scheduled', 'live', 'ended']) => {
  const organisationId = useOrgId();
  const where = useMemo(() => buildWhere(organisationId, statuses), [organisationId, statuses]);

  const { data, loading, error, refetch } = useQuery(LIVE_SESSIONS_QUERY, {
    ...defaultOptions,
    variables: { organisationId, limit, offset: 0, where },
    skip: !organisationId || !where,
  });

  return {
    sessions: data?.liveSessions || [],
    total: data?.liveSessionsAggregate?.agg_count || 0,
    loading,
    error,
    refetch,
  };
};

export const useLiveSessionById = (sessionId) => {
  const organisationId = useOrgId();

  const { data, loading, error, refetch } = useQuery(GET_LIVE_SESSION_BY_ID, {
    ...defaultOptions,
    variables: { id: sessionId, organisationId },
    skip: !sessionId || !organisationId,
  });

  return {
    session: data?.liveSessions?.[0] || null,
    loading,
    error,
    refetch,
  };
};

export const useLiveVideoRecording = (liveSessionId) => {
  const { data, loading, error, refetch } = useQuery(GET_LIVE_VIDEO_RECORDING, {
    ...defaultOptions,
    variables: { liveSessionId: liveSessionId ?? '' },
    skip: !liveSessionId,
  });

  return {
    recordings: data?.liveSessionsRecordings ?? [],
    loading,
    error,
    refetch,
  };
};
